/**
 * Display helpers. Score band logic mirrors BUILD.md §8 exactly, so the app and
 * the report never disagree about what "Optimal" means.
 */

export type BandKey = 'optimal' | 'needs_work' | 'high_risk';

export function bandOf(score: number | null | undefined): BandKey | null {
  if (score === null || score === undefined) return null;
  if (score >= 80) return 'optimal';
  if (score >= 60) return 'needs_work';
  return 'high_risk';
}

export const BAND_LABEL: Record<BandKey, string> = {
  optimal: 'Optimal',
  needs_work: 'Needs Work',
  high_risk: 'High Risk',
};

export const BAND_COLOR: Record<BandKey, string> = {
  optimal: 'var(--band-optimal)',
  needs_work: 'var(--band-warn)',
  high_risk: 'var(--band-risk)',
};

export function bandColor(score: number | null | undefined): string {
  const band = bandOf(score);
  return band ? BAND_COLOR[band] : '#9AA3AF';
}

/** Tailwind classes for a band pill. */
export function bandPillClass(score: number | null | undefined): string {
  switch (bandOf(score)) {
    case 'optimal':
      return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
    case 'needs_work':
      return 'bg-amber-50 text-amber-700 ring-amber-200';
    case 'high_risk':
      return 'bg-red-50 text-red-700 ring-red-200';
    default:
      return 'bg-slate-100 text-slate-600 ring-slate-200';
  }
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-07-21` → `Jul 21, 2026`. Fixed format, not locale-dependent. */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '-';
  const date = typeof value === 'string' ? new Date(value.length === 10 ? `${value}T00:00:00Z` : value) : value;
  if (Number.isNaN(date.getTime())) return '-';
  return `${MONTHS_SHORT[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '-';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '-';
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${formatDate(date)} · ${hours}:${minutes} UTC`;
}

/** `2026-07` → `Jul` for chart axes. */
export function formatMonth(value: string): string {
  const [, month] = value.split('-');
  const index = Number(month) - 1;
  return MONTHS_SHORT[index] ?? value;
}

export function formatDelta(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  if (value === 0) return '0';
  return value > 0 ? `+${value}` : String(value);
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function fullName(person: { firstName: string; lastName: string }): string {
  return `${person.firstName} ${person.lastName}`;
}

export function initials(person: { firstName: string; lastName: string }): string {
  return `${person.firstName[0] ?? ''}${person.lastName[0] ?? ''}`.toUpperCase();
}

/** Whole years from a date of birth to today. */
export function ageFromDob(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const born = new Date(dob);
  if (Number.isNaN(born.getTime())) return null;
  const now = new Date();
  let years = now.getUTCFullYear() - born.getUTCFullYear();
  const monthDelta = now.getUTCMonth() - born.getUTCMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getUTCDate() < born.getUTCDate())) years -= 1;
  return years >= 0 ? years : null;
}

export const STATUS_LABEL: Record<string, string> = {
  UPLOADED: 'Uploaded',
  EXTRACTED: 'Awaiting review',
  VERIFIED: 'Verified',
  SCORED: 'Scored',
  REPORTED: 'Completed',
};

export function statusPillClass(status: string): string {
  switch (status) {
    case 'REPORTED':
      return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
    case 'SCORED':
    case 'VERIFIED':
      return 'bg-blue-50 text-blue-700 ring-blue-200';
    case 'EXTRACTED':
      return 'bg-amber-50 text-amber-700 ring-amber-200';
    default:
      return 'bg-slate-100 text-slate-600 ring-slate-200';
  }
}
